<template>
  <div class="ui-select">
    <Listbox :model-value="modelValue" @update:model-value="updateValue">
      <ListboxButton class="ui-select__button !hidden md:!flex">
        <span class="block truncate">{{ modelValue?.label || title }}</span>

        <IconChevronDown />
      </ListboxButton>

      <transition name="fade">
        <ListboxOptions class="ui-select__options !hidden md:!block">
          <ListboxOption
            v-slot="{ active, selected }"
            v-for="option in options"
            :key="option.value"
            :value="option"
            as="template"
          >
            <li
              :class="{
                active: active,
                selected: selected,
                'ui-select__option text-secondary-nav': true,
              }"
            >
              <span class="ui-select__option-label">{{ option.label }}</span>

              <span v-if="selected" class="ui-select__option-icon"
                ><IconCheck
              /></span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </Listbox>

    <button class="ui-select__button md:!hidden" @click="openModal">
      <span class="block truncate">{{ modelValue?.label || title }}</span>

      <IconChevronDown />
    </button>

    <UiModalBottom v-model="modalOpen" class="ui-select__modal">
      <template #header>
        <span class="ui-select__modal-title">{{ title }}</span>
      </template>

      <Listbox :model-value="modelValue" @update:model-value="updateValue">
        <ListboxOptions class="ui-select__modal-options" :static="true">
          <ListboxOption
            v-slot="{ active, selected }"
            v-for="option in options"
            :key="option.value"
            :value="option"
            as="template"
          >
            <li
              :class="{
                active: active,
                selected: selected,
                'ui-select__modal-option': true,
              }"
            >
              <span class="ui-select__modal-option-label">{{
                option.label
              }}</span>

              <span v-if="selected" class="ui-select__option-icon"
                ><IconCheck
              /></span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </Listbox>

      <div class="row mt-auto">
        <div class="col-6">
          <UiButton @click="closeModal" variant="outlined" full-width
            >Cancel</UiButton
          >
        </div>

        <div class="col-6">
          <UiButton @click="submitModal" full-width>Submit</UiButton>
        </div>
      </div>
    </UiModalBottom>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue'
import UiModalBottom from './UiModalBottom.vue'
import UiButton from './UiButton.vue'
import IconCheck from './icons/IconCheck.vue'
import IconChevronDown from './icons/IconChevronDown.vue'

type Option = {
  label: string
  value: string
}

type Props = {
  title: string
  options: Option[]
  modelValue: any
  fullWidth?: boolean
}

// Setup emit
const emit = defineEmits(['update:modelValue'])

const props = defineProps<Props>()

const selected = ref<Option>(props.options[0])
const modalOpen = ref(false)
const modalSelected = ref<Option>(selected.value)

watch(selected, (value) => (modalSelected.value = value))

const openModal = () => {
  if (modalSelected.value !== selected.value) {
    modalSelected.value = selected.value
  }

  modalOpen.value = true
}

const closeModal = () => (modalOpen.value = false)

const submitModal = () => {
  selected.value = modalSelected.value
  closeModal()
}

const closeModalOnDesktop = () => {
  const mql = window.matchMedia('(min-width: 768px)')

  if (mql.matches) {
    closeModal()
  }
}

onMounted(() => {
  window.addEventListener('resize', closeModalOnDesktop)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', closeModalOnDesktop)
})

const updateValue = (value) => {
  emit('update:modelValue', value)
}
</script>

<style lang="scss">
@import '../styles/config';

.ui-select {
  display: inline-flex;
  @include media-breakpoint-up(lg) {
    z-index: 1;
  }
}

.ui-select--full-width {
  display: flex;
  width: 100%;
}

.ui-select__button {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem;
  font-family: $font-barlow;
  font-size: 0.875rem;
  line-height: 1.14285714;
  color: $black;
  background-color: $gray-100;
  border: 1px solid $gray-200;
  border-radius: 0.125rem;
  user-select: none;
  cursor: pointer;
  @include transition('color, background-color, border-color');

  &:hover {
    background-color: $gray-200;
  }

  &:focus {
    outline: none;
    color: $white;
    background-color: $black;
    border-color: $black;
  }

  span {
    margin-right: 0.25rem;
  }

  .svg-icon {
    position: relative;
    width: 1rem;
    top: 1px;
    margin-left: auto;
  }

  @include media-breakpoint-up(md) {
    padding: 0.6875rem 0.75rem;
    font-size: 1rem;
    line-height: 1.25;

    span {
      margin-right: 0.5rem;
    }

    .svg-icon {
      width: 1.25rem;
    }
  }
}

.ui-select__options {
  position: absolute;
  top: 100%;
  left: 0;
  width: 18.75rem;
  margin-top: 5px;
  padding: 0.75rem;
  color: $black;
  list-style: none;
  background-color: $white;
  border: 1px solid $gray-100;
  border-radius: 0.5rem;
  box-shadow: 0.125rem 0.625rem 2rem 0 rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.ui-select__option {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 0.25rem;
  padding: 0.625rem 3.375rem 0.625rem 0.625rem;
  border-radius: 0.125rem;
  cursor: pointer;
  @include transition('background-color');

  &:last-child {
    margin-bottom: 0;
  }

  &-icon {
    position: absolute;
    top: 50%;
    right: 0.625rem;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1.5rem;
    height: 1.5rem;
    color: $white;
    background-color: $black;
    border-radius: 50%;
    transform: translateY(-50%);

    svg {
      width: 1rem;
    }
  }

  &.active {
    background-color: $gray-100;
  }
}

.ui-select__modal {
  .ui-modal-bottom__content {
    display: flex;
    flex-direction: column;
    padding: 0.75rem;
  }
}

.ui-select__modal-title {
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.015em;
  color: $black;
}

.ui-select__modal-options {
  margin: 0 0 1.5rem;
  padding: 0;
  list-style: none;
  border-top: 1px solid $gray-200;
}

.ui-select__modal-option {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 3.25rem;
  font-size: 1.0625rem;
  font-weight: 500;
  line-height: 1.375;
  letter-spacing: -0.00588235em;
  color: $black;
  border-bottom: 1px solid $gray-200;
  cursor: pointer;
  @include transition('color');

  &:hover {
    color: $red;
    text-decoration: none;
  }
}
</style>
